import { useEffect, useRef, useState } from "react";
import type { Strings } from "../i18n/strings";
import type { Confession, InputMode, Lang } from "../lib/types";
import { playChirp, warmupSound } from "../lib/sound";
import { isNativeSTT, startNativeSTT, stopNativeSTT } from "../lib/speech";

const LANGS: { id: Lang; label: string }[] = [
  { id: "ko", label: "한국어" },
  { id: "en", label: "EN" },
  { id: "ja", label: "日本語" },
];

type SpeechRecognitionLike = {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((e: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
};

function getRecognition(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

interface Props {
  t: Strings;
  lang: Lang;
  setLang: (l: Lang) => void;
  onSettings: () => void;
  onSubmit: (c: Confession) => void;
}

export default function Home({ t, lang, setLang, onSettings, onSubmit }: Props) {
  const [text, setText] = useState(() => localStorage.getItem("hyuh_draft") ?? "");
  const [listening, setListening] = useState(false);
  const modeRef = useRef<InputMode>("text");
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const ready = text.trim().length >= 1;

  useEffect(() => {
    localStorage.setItem("hyuh_draft", text);
  }, [text]);

  function micLang(): string {
    return lang === "ko" ? "ko-KR" : lang === "ja" ? "ja-JP" : navigator.language || "en-US";
  }

  async function toggleMic() {
    if (listening) {
      playChirp(false);
      if (isNativeSTT()) await stopNativeSTT();
      else recRef.current?.stop();
      setListening(false);
      return;
    }
    modeRef.current = "voice";

    if (isNativeSTT()) {
      const base = text;
      const ok = await startNativeSTT(
        micLang(),
        (full) => setText(base ? `${base} ${full}` : full),
        () => setListening(false),
      );
      if (ok) {
        playChirp(true);
        setListening(true);
      }
      return;
    }

    const rec = getRecognition();
    if (!rec) return;
    playChirp(true);
    rec.lang = micLang();
    rec.continuous = true;
    rec.interimResults = false;
    const base = text;
    rec.onresult = (e) => {
      let chunk = "";
      for (let i = 0; i < e.results.length; i++) chunk += e.results[i][0].transcript;
      setText((base ? base + " " : "") + chunk);
    };
    rec.onend = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  }

  function submit() {
    if (!ready) return;
    warmupSound();
    recRef.current?.stop();
    onSubmit({ mode: modeRef.current, text: text.trim() });
  }

  return (
    <div className="screen home">
      <div className="home-ambient" />
      <div className="topbar">
        <span />
        <button className="gear" onClick={onSettings} aria-label="settings">
          ⚙
        </button>
      </div>

      <div className="home-prompt">{t.homePrompt}</div>
      <p className="home-flavor">{t.homeFlavor}</p>

      <div className="input-card">
        <textarea
          className="confess-area"
          value={text}
          placeholder={t.placeholder}
          onChange={(e) => {
            modeRef.current = "text";
            setText(e.target.value);
          }}
        />
        <div className="mic-row">
          <span className="mic-hint">{listening ? t.listening : t.micHint}</span>
          <button className={`mic-btn ${listening ? "on" : ""}`} onClick={toggleMic} aria-label="mic">
            <span className="mic-cap" />
          </button>
        </div>
      </div>

      <button className="btn-primary" onClick={submit} disabled={!ready}>
        {ready ? t.submit : t.keepGoing}
      </button>

      <div className="lang-seg">
        {LANGS.map((L) => (
          <button key={L.id} className={`lang-chip ${lang === L.id ? "on" : ""}`} onClick={() => setLang(L.id)}>
            {L.label}
          </button>
        ))}
      </div>
    </div>
  );
}
